<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal
        size="md" :visible="modal.show" :data="model" :title="$t('Bulk Edit')"
        :ok-title="$t('Submit')" :is-loading="loading" :content-in-form="false" scrollable
        :show-confirmation="showConfirmation" @hide="beforeHide"
        @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)">
      <spinner show v-if="loading" height="20"/>
      <b-form v-else @submit.prevent="handleSubmit(onSubmit)">
        <button type="submit" hidden/>
        <b-alert variant="danger" :show="error && error.length > 0">
          {{ error }}
        </b-alert>
        <div class="row" v-if="!isSalesTaxEnabled">
          <div class="col-2 pl-0 text-center">
            <label>{{ $t('Clear') }}</label>
            <field-title-hint target="clear-checkbox"
                              :title="$t('If the \'Clear\' checkbox is chosen, the value(s) for the chosen field(s) will be cleared.')">
            </field-title-hint>
            <input-widget
                class="mt-1" :model="model" attribute="empty_tax_status_uuid" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'tax_status_uuid')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <input-widget
                :model="model" attribute="tax_status_uuid" type="select2" display-field="${item.name}" hint title-hint
                :items="taxStatuses" @select2Select="onInputChange('empty_tax_status_uuid')">
            </input-widget>
          </div>
        </div>
        <div v-if="enableContinuousAuction && !isSalesTaxEnabled" class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_tax_rate" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'tax_rate')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <input-widget
                :model="model" attribute="tax_rate" type="number" hint title-hint
                @input="onInputChange('empty_tax_rate')">
            </input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_receive_date" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'receive_date')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <input-widget
                :model="model" type="datepicker" attribute="receive_date" title-hint
                @change="onInputChange('empty_receive_date')">
            </input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox"/>
          <div class="col-10 pl-0">
            <input-widget
                :model="model" attribute="quantity" type="number" title-hint @input="onInputChange('empty_quantity')">
            </input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox"/>
          <div class="col-10 pl-0">
            <item-location-select-input-widget
                :model="model" attribute="item_location_uuid" title-hint
                @select="onInputChange('empty_item_location_uuid')">
            </item-location-select-input-widget>
          </div>
        </div>
        <div class="row mt-2">
          <div class="col-2 pl-0 text-center">
            <input-widget
                :model="model" attribute="empty_collection_info_enabled" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'collection_info_enabled')">
            </input-widget>
          </div>
          <div class="col-10 pl-0 d-flex align-items-center">
            <input-widget
                :model="model" attribute="collection_info_enabled" type="checkbox" :is-switch="true" size="lg"
                title-hint @change="onCollectionInformationChange">
            </input-widget>
          </div>
          <div class="card-body pb-0" v-if="model.collection_info_enabled">
            <div class="row">
              <div class="row">
                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_address_1" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_address_1')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_address_1" title-hint
                      @input="onInputChange('empty_collection_info_address_1')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_address_2" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_address_2')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_address_2" title-hint
                      @input="onInputChange('empty_collection_info_address_2')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_state" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'collection_info_state')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_state" title-hint
                      @input="onInputChange('empty_collection_info_state')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_city" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_city')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_city" title-hint
                      @input="onInputChange('empty_collection_info_city')">
                  </input-widget>
                </div>
                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_municipality" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_municipality')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_municipality" title-hint
                      @input="onInputChange('empty_collection_info_municipality')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_zip_code" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_zip_code')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_zip_code" title-hint @keyup="toUpper"
                      @input="onInputChange('empty_collection_info_zip_code')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_phone" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'collection_info_phone')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_phone" title-hint
                      @input="onInputChange('empty_collection_info_phone')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_email" type="checkbox" :label="false"
                      size="lg" @change="onEmptyClick($event, 'collection_info_email')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_email" title-hint
                      @input="onInputChange('empty_collection_info_email')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_notes" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'collection_info_notes')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_notes" title-hint
                      @input="onInputChange('empty_collection_info_notes')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_longitude" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'longitude')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      type="number"
                      :model="model" attribute="longitude" title-hint
                      @input="onInputChange('empty_longitude')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_latitude" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'latitude')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      type="number"
                      :model="model" attribute="latitude" title-hint
                      @input="onInputChange('empty_latitude')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_first_name" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'collection_info_first_name')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_first_name" title-hint
                      @input="onInputChange('empty_collection_info_first_name')">
                  </input-widget>
                </div>

                <div class="col-2 pl-0 empty-checkbox">
                  <input-widget
                      :model="model" attribute="empty_collection_info_last_name" type="checkbox" :label="false" size="lg"
                      @change="onEmptyClick($event, 'collection_info_last_name')">
                  </input-widget>
                </div>
                <div class="col-10 pl-0">
                  <input-widget
                      :model="model" attribute="collection_info_last_name" title-hint
                      @input="onInputChange('empty_collection_info_last_name')">
                  </input-widget>
                </div>

              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_account_managers_uuid" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'account_managers_uuid')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <user-select-input-widget
                :model="model" attribute="account_managers_uuid" is-search title-hint
                @select="onInputChange('empty_account_managers_uuid')">
            </user-select-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_rep_user_uuid" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'rep_user_uuid')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <user-select-input-widget
                :model="model" attribute="rep_user_uuid" title-hint is-search
                @select="onInputChange('empty_rep_user_uuid')">
            </user-select-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_expert_user_uuid" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'expert_user_uuid')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <user-select-input-widget
                :model="model" attribute="expert_user_uuid" is-search title-hint
                @select="onInputChange('empty_expert_user_uuid')">
            </user-select-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_low" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'low')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <currency-input-widget
                :model="model" attribute="low" title-hint :append-question="getAmountFieldQuestionText('low')"
                @input="onInputChange('empty_low')">
            </currency-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_high" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'high')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <currency-input-widget
                :model="model" attribute="high" title-hint :append-question="getAmountFieldQuestionText('high')"
                @input="onInputChange('empty_high')">
            </currency-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_start_price" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'start_price')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <currency-input-widget
                :model="model" attribute="start_price" :rules="startPriceRules" title-hint
                @input="onInputChange('empty_start_price')"
                :append-question="getAmountFieldQuestionText('start_price')">
            </currency-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_reserve" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'reserve')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <currency-input-widget
                :model="model" attribute="reserve" title-hint :append-question="getAmountFieldQuestionText('reserve')"
                @input="onInputChange('empty_reserve')">
            </currency-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_buy_now_price" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'buy_now_price')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <currency-input-widget
                :model="model" attribute="buy_now_price" title-hint
                :append-question="getAmountFieldQuestionText('buy_now_price')"
                @input="onInputChange('empty_buy_now_price')">
            </currency-input-widget>
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_cataloguing_lifecycle" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'cataloguing_lifecycle')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <cataloguing-lifecycle-select-input-widget :model="model"
              attribute="cataloguing_lifecycle" @select2Select="onInputChange('empty_cataloguing_lifecycle')" title-hint
            />
          </div>
        </div>
        <div class="row">
          <div class="col-2 pl-0 empty-checkbox">
            <input-widget
                :model="model" attribute="empty_photography_lifecycle" type="checkbox" :label="false" size="lg"
                @change="onEmptyClick($event, 'photography_lifecycle')">
            </input-widget>
          </div>
          <div class="col-10 pl-0">
            <photography-lifecycle-select-input-widget
              attribute="photography_lifecycle"
              :model="model"
              @select2Select="onInputChange('empty_photography_lifecycle')"
            title-hint
            />
          </div>
        </div>
        <template v-for="field in model.sorted_dynamic_fields_template">
          <div class="row mb-3"  :key="field.identifier">
            <div class="col-2 pl-0 empty-checkbox">
              <input-widget v-if="!hideIsEmpty[field.identifier]"
                            :model="model" :attribute="`empty_dynamic_fields_${field.key}`" type="checkbox"
                            :label="false" size="lg"
                            @change="onEmptyDynamicFieldClick($event, field)">
              </input-widget>
            </div>
            <div class="col-10 pl-0">
              <input-widget
                :key="`${field.identifier}_${masterLanguage.key}`"
                :model="model"
                :attribute="`${field.identifier}_${masterLanguage.key}`"
                :type="field.type" :rules="getRulesByKey(field.key)"
                :char-counter="field.hasCharCounter()"
                :label="field.label"
                :tags="field.isAppendable()"
                :multiple="field.isMultiple()"
                :items="field.getChoices(masterLanguage.key)" display-field="${item.key}"
                value-field="value"
                :class="field.type === 'checkbox' ? 'mt-4' : ''"
                @input="onDynamicFieldInputChange($event, masterLanguage.key, field)"
                @select2Select="onDynamicFieldSelectionChange($event, field, masterLanguage.key)"
                @select2Clear="onDynamicFieldSelectionClear($event, field, masterLanguage.key)"
                @change="onDynamicFieldChange($event,  masterLanguage.key, field)"
                @select2Unselect="onDynamicFieldUnselect($event, field, masterLanguage.key)"
                @blur="onDynamicFieldBlur($event, field.type)"/>
              <small v-if="Object.keys(categories).length !== field.categories.length">
                {{ $t('Categories:') }}
                <span class="badge badge-secondary ml-1" v-for="cat in field.categories">
                {{ categories[cat].name | masterLanguage }}
              </span>
              </small>
            </div>
          </div>
          <hr>
        </template>

      </b-form>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import uuidV4 from 'uuid';
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import ItemLocationSelectInputWidget from "../../core/widgets/ItemLocationSelectInputWidget";
  import InventoryBulkEditModel from "./InventoryBulkEdit.model";
  import {createNamespacedHelpers} from "vuex";
  import _, {isArray} from "lodash";
  import {WS_INVENTORY_BULK_EDIT_PROGRESS} from "../constants";
  import {ProgressMixin} from "@/core/mixins/progress.mixin";
  import FieldTitleHint from "./components/FieldTitleHint";
  import Spinner from "@/core/components/common/Spinner";
  import CategoryService from "@/modules/setup/category/CategoryService";
  import i18nHelper from "@/core/services/i18n.service";
  import CataloguingLifecycleSelectInputWidget from "@/core/widgets/CataloguingLifecycleSelectInputWidget.vue";
  import PhotographyLifecycleSelectInputWidget from "@/core/widgets/PhotographyLifecycleSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('inventory');
  const {mapState: mapStateGlobal, mapActions: mapGlobalActions} = createNamespacedHelpers('global');

  export default {
    name: "InventoryBulkEditModel",
    components: {
      PhotographyLifecycleSelectInputWidget,
      CataloguingLifecycleSelectInputWidget,
      Spinner,
      FieldTitleHint,
      ItemLocationSelectInputWidget,
      CurrencyInputWidget,
      UserSelectInputWidget,
      InputWidget,
      ArtisioModal,
    },
    mixins: [ProgressMixin],
    data() {
      return {
        model: new InventoryBulkEditModel(),
        showConfirmation: true,
        progress: null,
        categories: {},
        masterLanguage: i18nHelper.getMasterLanguage(),
        hideIsEmpty: {}
      }
    },
    watch: {
      async items() {
        const distinctCategoryUuids = [];
        if (this.items && this.items.length > 0) {
          for (let item of this.items) {
            if (distinctCategoryUuids.indexOf(item.category_uuid) === -1) {
              distinctCategoryUuids.push(item.category_uuid);
            }
          }
        }
        const bulkEditFields = await CategoryService.getCategoryBulkEditFields(distinctCategoryUuids)
        const dynamicFieldsData = bulkEditFields.body.data;
        this.categories = dynamicFieldsData.categories;
        const dynamicFields = this.mergeDynamicFieldsDynamicFields(dynamicFieldsData.fields);
        Object.values(dynamicFields).forEach(field => {
          if (field.rules && 'required' in field.rules && field.rules.required) {
            field.rules.required = false
            this.hideIsEmpty[field.identifier] = true;
          }
        })

        this.model.initDynamicFields(this.model, dynamicFields)
        if (!this.disableItemEditOnSold) {
          return false
        }

        if (this.items && this.items.length > 0) {
          let erroredStockNos = []

          for (let item of this.items) {
            if (item && item.reservations && item.reservations.filter(r => r.status === 'sold' && r.auction && r.auction.status === 'completed').length > 0) {
              erroredStockNos.push(item.stock_no)
              continue
            }

            if (item.financial_items && item.financial_items.filter(fi => fi.invoice && fi.invoice.status === 'paid').length > 0) {
              erroredStockNos.push(item.stock_no)
            }
          }

          if (erroredStockNos.length > 0) {
            this.$errorDialog(this.$i18n.t('You cannot bulk edit item(s) which are sold through a sale or are manually sold. Please, remove following item(s) from the selected list and try again: {items}', {items: erroredStockNos.join(', ')}))
            this.hideBulkEditModal()
          }

        }
      },
    },
    computed: {
      ...mapStateGlobal(['taxStatuses', 'generalRulesAndRestrictions', 'enableContinuousAuction', 'isSalesTaxEnabled', 'disableItemEditOnSold', 'locales']),
      ...mapState({
        modal: state => state.bulkEditModal,
        error: state => state.bulkEditModal.error,
        items: state => state.bulkEditModal.items,
        dataLoading: state => state.bulkEditModal.loading,
      }),
      loading() {
        return this.dataLoading || !!this.progress
      },
      startPriceRules() {
        if (this.generalRulesAndRestrictions.start_price_lower_than_reserve && this.model.reserve) {
          return `max_value:${this.model.reserve - 1}|min_value:0`;
        }
        return `min_value:0`;
      },
    },
    methods: {
      ...mapActions(['hideBulkEditModal', 'inventoryBulkUpdate', 'getTableData']),
      ...mapGlobalActions(['addProgress', 'removeProgress', 'updateProgressValue']),
      mergeDynamicFieldsDynamicFields(fields) {
        const dynamicFields = {};

        if (!fields || !Array.isArray(fields)) {
          return dynamicFields;
        }

        // Helper function to create a unique key for a field (excluding category_uuid)
        const getFieldKey = (field) => {
          const fieldCopy = {...field};
          delete fieldCopy.category_uuid;
          // Sort keys to ensure consistent comparison
          const sortedKeys = Object.keys(fieldCopy).sort();
          return JSON.stringify(sortedKeys.reduce((acc, key) => {
            acc[key] = fieldCopy[key];
            return acc;
          }, {}));
        };

        // Group fields by their unique key (excluding category_uuid)
        const fieldGroups = new Map();

        fields.forEach(field => {
          const uniqueKey = getFieldKey(field);

          if (!fieldGroups.has(uniqueKey)) {
            fieldGroups.set(uniqueKey, {
              fieldData: {...field},
              categoryUuids: []
            });
          }

          // Add category_uuid to the array if not already present
          const group = fieldGroups.get(uniqueKey);
          if (field.category_uuid && !group.categoryUuids.includes(field.category_uuid)) {
            group.categoryUuids.push(field.category_uuid);
          }
        });

        // Convert Map to array, sort by position, then build dynamicFields object
        const sortedGroups = Array.from(fieldGroups.values()).sort((a, b) => {
          const positionA = a.fieldData.position || 0;
          const positionB = b.fieldData.position || 0;
          return positionA - positionB;
        });

        // Track how many times we've seen each field.key to handle duplicates
        const keyCounts = {};

        // Build dynamicFields object with merged fields in sorted order
        sortedGroups.forEach((group) => {
          const mergedField = {
            ...group.fieldData,
            categories: group.categoryUuids,
            'identifier': `${group.fieldData.key}_${uuidV4()}`
          };

          if (mergedField.key) {
            // If we've seen this key before, append a suffix to make the object key unique
            // but preserve the original key in the field data
            if (keyCounts[mergedField.key] !== undefined) {
              keyCounts[mergedField.key]++;
              const uniqueObjectKey = `${mergedField.key}_${keyCounts[mergedField.key]}`;
              dynamicFields[uniqueObjectKey] = mergedField;
            } else {
              // First occurrence of this key
              keyCounts[mergedField.key] = 0;
              dynamicFields[mergedField.key] = mergedField;
            }
          }
        });

        return dynamicFields;
      },
      toUpper(e) {
        this.model.collection_info_zip_code = e.target.value.toUpperCase();
      },
      onShow() {
        this.showConfirmation = true;
        this.$ws.listen(`inventories.${WS_INVENTORY_BULK_EDIT_PROGRESS}`, this.onProgressNotificationReceive);
      },
      beforeHide($event) {
        this.showConfirmation = false;
        if (this.progress) {
          $event.preventDefault();
        }
        this.showConfirmation = true;
      },
      onHidden() {
        this.model = new InventoryBulkEditModel();
        this.hideBulkEditModal();
      },
      async onSubmit() {
        this.model.resetErrors();
        if ((this.model.tax_status_uuid || this.model.empty_tax_status_uuid) || (this.enableContinuousAuction && (this.model.tax_rate || this.model.empty_tax_rate))) {
          let success = await this.$confirm(this.$i18n.t('Changing terms will trigger automatic recalculation of all associated invoices and settlements. Would you like to proceed?'));
          if (!success) {
            return
          }
        }
        const data = _.cloneDeep(this.model.toJSON());
        let items = _.cloneDeep(this.items.map(item => {
          item.account_managers_uuid = item.account_managers && item.account_managers.length > 0 ? item.account_managers.map(am => am.uuid) : [];

          return item;
        }));

        for (let fieldName in data) {
          // Change property by new value
          if (fieldName === 'dynamic_fields') {
            continue;
          }
          if (!['', null].includes(data[fieldName])) {
            items = items.map(item => {
              const value = ['account_managers_uuid'].includes(fieldName) ? [data[fieldName]] : data[fieldName];

              return {...item, [fieldName]: value}
            })
          }

          // Make property null if empty checkbox is True
          if (data[`empty_${fieldName}`]) {
            items = items.map(item => {
              const value = ['account_managers_uuid'].includes(fieldName) ? [] : ['collection_info_enabled'].includes(fieldName) ? false : null;

              return {...item, [fieldName]: value}
            })
          }

          // Delete empty properties from model
          const names = fieldName.split('_');
          if (names && names[0] === 'empty') {
            delete data[fieldName];
          }
        }

        // Change all other data if necessary for back
        items = items.map(item => {
          // Make collection all property null if empty checkbox is True
          if (data['collection_info_enabled'] === false) {
            item = {
              ...item,
              collection_info_address_1: null,
              collection_info_address_2: null,
              collection_info_state: null,
              collection_info_city: null,
              collection_info_zip_code: null,
              collection_info_phone: null,
              collection_info_first_name: null,
              collection_info_last_name: null,
              collection_info_email: null,
              collection_info_notes: null,
              collection_info_municipality: null,
              longitude: null,
              latitude: null,
            }
          }
          this.model.sorted_dynamic_fields_template.forEach(sdft => {
            if (sdft.categories.indexOf(item.category_uuid) === -1) {
                return;
            }
            if (this.model.empty_dynamic_fields[sdft.identifier] === true) {
              Object.keys(this.locales.available_locales).forEach(l => {
                if (!item.dynamic_fields[l]) {
                  item.dynamic_fields[l] = {};
                }
                item.dynamic_fields[l][sdft.key] = null;
              });
            } else if (this.model.dynamic_fields[`${sdft.identifier}_${this.masterLanguage.key}`]) {
              Object.keys(this.locales.available_locales).forEach(l => {
                if (!item.dynamic_fields[l]) {
                  item.dynamic_fields[l] = {};
                }
                item.dynamic_fields[l][sdft.key] = this.model.dynamic_fields[`${sdft.identifier}_${this.masterLanguage.key}`];
              });
            }
          })

          // Add dates for reservations during continuous auctions
          if (this.enableContinuousAuction) {
            item.reservations = item.reservations.map(reservation => {
              if (reservation.auction && reservation.auction.dates) {
                reservation.dates = [reservation.auction.dates[0]];
              }
              return reservation;
            })
          }
          return item;
        })
        const {success, body, toastError} = await this.inventoryBulkUpdate(items);

        if (success) {
          await this.addProgressIfNotExist(this.$i18n.t('Inventory bulk edit started'));
        } else {
          this.removeProgressIfExist();
          if (toastError) {
            this.$errorDialog(toastError);
          } else {
            if (body.non_field_errors) {
              this.$errorDialog(body.non_field_errors[0]);
            } else {
              this.model.setMultipleErrors(body);
            }
          }
        }
      },
      getAmountFieldQuestionText(attribute) {
        let fieldName = this.$options.filters.removeUnderscores(attribute);
        fieldName = this.$options.filters.toUpperCaseFirst(fieldName);
        return this.$i18n.t("{fieldName} amount given for single quantity of the item", {fieldName})
      },
      onEmptyClick(event, field) {
        if (field === 'collection_info_enabled') {
          this.model[field] = event === true ? false : null;
          this.resetCollectionInfo();
        } else {
          this.model[field] = null;
        }
      },
      onInputChange(field) {
        this.model[field] = false;
      },
      onCollectionInformationChange(collection_enabled) {
        this.model['empty_collection_info_enabled'] = false;
        if (collection_enabled === false) {
          this.model.collection_info_enabled = null;
          this.resetCollectionInfo();
        }
      },
      resetCollectionInfo() {
        this.model.collection_info_address_1 = null;
        this.model.collection_info_address_2 = null;
        this.model.collection_info_state = null;
        this.model.collection_info_city = null;
        this.model.collection_info_zip_code = null;
        this.model.collection_info_phone = null;
        this.model.collection_info_first_name = null;
        this.model.collection_info_last_name = null;
        this.model.collection_info_email = null;
        this.model.collection_info_notes = null;
        this.model.collection_info_municipality = null;
        this.model.longitude = null;
        this.model.latitude = null;
      },
      async onProgressNotificationReceive(data) {
        const errors = _.cloneDeep(data.errors);
        if (errors) {
          this.removeProgressIfExist();
          if (isArray(errors)) {
            errors.forEach(err => this.model.setMultipleErrors(err));
          } else if (errors.non_field_errors) {
            const listItemsArr = [];
            errors.non_field_errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          } else if (errors.validation_errors) {
            errors.validation_errors.forEach(err => this.model.setMultipleErrors(err));
          } else if (errors.errors) {
            const listItemsArr = [];
            errors.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr));
          }
        } else {
          await this.handleProgressNotification(data,
              this.$i18n.t('Inventory bulk edit finished successfully'),
              this.$i18n.t('Validation Error'),
              this.$i18n.t('Something went wrong while updating inventory. Please, try again later.'));

          if (!this.progress) {
            this.showConfirmation = false;
            this.hideBulkEditModal();
          }
        }
      },
      onEmptyDynamicFieldClick(event, field) {
        this.model.empty_dynamic_fields[field.identifier] = event;
        if (event) {
          this.assignDynamicFieldValuesToLocales(null, field.identifier, field.type)
        }
      },
      getRulesByKey(key) {
        if (this.rules && this.rules[key]) {
          return this.rules[key]
        }
        return null
      },
      assignDynamicFieldValuesToLocales(val, field_key, type) {
        this.model.dynamic_fields[`${field_key}`] = type === 'select2' ? val.value : (type === 'date' && val === '') ? null : val;
        this.model.dynamic_fields[`${field_key}_${this.masterLanguage.key}`] = type === 'select2' ? val.value : (type === 'date' && val === '') ? null : val;
        this.model[`${field_key}_${this.masterLanguage.key}`] = type === 'select2' ? val.value : (type === 'date' && val === '') ? null : val;
      },
      onDynamicFieldInputChange(val, locale, field) {
        let type = field.type;
        if (!field.translatable) {
          this.assignDynamicFieldValuesToLocales(val, `${field.identifier}`, field.type);
        }
        val = type === 'date' && val === '' ? null : val;
      },
      onDynamicFieldSelectionChange(value, field, localeKey) {
        const key = field.identifier;

        if (field.isMultiple()) {
          if (!this.model[key][localeKey]) {
            this.model.dynamic_fields[key] = [];
          }
          this.model.dynamic_fields[key].push(value.id);
        } else {
          this.assignDynamicFieldValuesToLocales(value, key, field.type);
        }
      },
      onDynamicFieldSelectionClear(value, field, localeKey) {
        const key = field.identifier;

        if (field.isMultiple()) {
          this.model.dynamic_fields[key] = [];
          this.model[`${key}_${localeKey}`] = [...this.model.dynamic_fields[key]];
        } else {
          this.assignDynamicFieldValuesToLocales(value, key, field.type);
        }
      },
     onDynamicFieldChange(val, locale, field) {
        let type = field.type;
        if (type === 'checkbox') {
          this.onDynamicFieldInputChange(val, locale, field);
        }
      },
      onDynamicFieldUnselect(value, field, localeKey) {
        if (field.isMultiple()) {
          const selection = this.model[field.identifier][localeKey];
          if (Array.isArray(selection)) {
            this.model.dynamic_fields[field.identifier] = selection.filter(item => item !== value.id);
            this.model[`${field.identifier}_${localeKey}`] = [...this.model.dynamic_fields[field.identifier]];
          }
        }
      },
      onDynamicFieldBlur(val, type) {
        val = type === 'date' && val === '' ? null : val;
      },
    },
    destroyed() {
      this.removeProgressIfExist();
      this.$ws.remove(`inventories.${WS_INVENTORY_BULK_EDIT_PROGRESS}`);
    },
  }
</script>

<style lang="scss" scoped>
  .collection-info-card {
    .card-header {
      /deep/ .form-group {
        margin-bottom: 0;
      }
    }
  }

  .empty-checkbox {
    margin-top: 2rem;
    text-align: center;
  }
</style>
